import { QueryClient, QueryCacheNotifyEvent } from '@tanstack/react-query'
import { USER_TOKEN_QUERIES_PREFIX } from '@/queries/hooks/use-user'
import { UserData, UserInitResponse } from '@/types/user'
import { verifyResult } from '@/utilities/utils'
import nearmapUsageTracker from './nearmapUsageTracker'

// Create a singleton instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 2,
    },
  },
})

const verifyUserData = (
  data: UserInitResponse
): data is UserInitResponse & { data: UserData } => {
  return verifyResult(data) && data.success && !!data.data
}

const defaultUser = (): UserData => ({
  UUID: '',
  isAdmin: false,
  isActive: false,
  canAccessQA: false,
  role: undefined,
  permissions: undefined,
  adminAccounts: [],
  profile: undefined,
  accounts: [],
  preferences: undefined,
  accountID: 0,
  mapID: 0,
  isLoggedIn: false,
})

// Encapsulated API for query client operations
export const queryClientManager = {
  // User data operations
  getUserData: () => {
    const rawData = queryClient.getQueryData<UserInitResponse>(['user', 'init'])
    if (!rawData) return null

    if (verifyUserData(rawData)) {
      const {
        profile,
        isAdmin,
        isActive,
        canAccessQA,
        role,
        permissions,
        adminAccounts,
        accounts = [],
        preferences,
        accountID,
        mapID,
      } = rawData.data

      const user = defaultUser()
      user.UUID = rawData.userUUID ?? ''

      // Handle account selection and validation
      if (!accounts.length) {
        throw new Error(
          'You are not a part of any accounts. Please contact support@myassetmap.com.'
        )
      }

      // Validate first account has an ID if we need to use it as fallback
      const firstValidAccount = accounts.find(acc => acc.id != null)
      if (!firstValidAccount) {
        throw new Error(
          'Invalid account data. Please contact support@myassetmap.com.'
        )
      }

      // Set accountID with fallback to first valid account if none specified
      user.accountID = accountID ?? firstValidAccount.id
      const account = accounts.find(acc => acc.id === user.accountID)

      if (!account) {
        throw new Error(
          'No active accounts have been found. Please have your admin add to you an account.'
        )
      }

      // Set user permissions and role based on account
      user.isAdmin = isAdmin || adminAccounts?.includes(user.accountID) || false
      user.isActive = isActive
      user.canAccessQA = canAccessQA ?? false
      user.role = role
      user.permissions = permissions
      user.adminAccounts = adminAccounts
      user.accounts = accounts

      // Handle map selection with fallbacks
      user.mapID = mapID ?? account.maps?.[0]?.id ?? null

      // Set remaining user data
      user.profile = profile
      user.preferences = preferences

      // Validate required data exists
      if (!user.UUID || !user.accountID || !user.mapID) {
        throw new Error(
          'Missing required user data. Please contact support@myassetmap.com.'
        )
      }

      user.isLoggedIn = true
      return user
    }
    return defaultUser()
  },

  setUserData: (data: UserInitResponse) =>
    queryClient.setQueryData(['user', 'init'], data),

  invalidateUserQueries: () => {
    queryClient.removeQueries({ queryKey: [...USER_TOKEN_QUERIES_PREFIX] })
    queryClient.invalidateQueries({ queryKey: ['user', 'init'] })
    queryClient.invalidateQueries({ queryKey: ['user', 'session'] })
  },

  // Cache subscription
  subscribeToCache: (callback: (event: QueryCacheNotifyEvent) => void) => {
    return queryClient.getQueryCache().subscribe(callback)
  },

  // Initialize cache subscriptions
  nearMapUsageTrackerQueryClientSubscription: () => {
    queryClientManager.subscribeToCache(event => {
      try {
        if (
          // only trigger nearmapUsageTracker.loginListener if the user has logged in at least once
          event.type === 'updated' &&
          event.query.queryKey[0] === 'user' &&
          event.query.queryKey[1] === 'init' &&
          event.action.type === 'success' &&
          (event.action.data as { success?: boolean } | undefined)?.success
        ) {
          nearmapUsageTracker.loginListener()
        }
      } catch (error) {
        console.error('Error subscribing to user query changes:', error)
      }
    })
  },
}

// Export the QueryClient instance for React Query Provider
export { queryClient }
